/**
 * The yearbook, at the top of the week.
 *
 * Once the last card is signed this is the first thing anybody wants and the
 * schedule below it is the last: the week is over, and what is left is who won
 * and what your own card said. So it sits above the days rather than behind a
 * tab, and it opens on YOU — a man wants his own line first and the champion
 * second, which is the one place a scoreboard and a yearbook disagree.
 *
 * Everything is derived from the same scores the board reads, so it cannot
 * drift from it. Before the championship is signed there is no champion and
 * this says so, for the same reason the Board refuses to crown anybody early.
 */

import { useState } from 'react'
import type { Tournament } from '../engine/types'
import { buildYearbook, yearbookText, type PlayerYear, type Yearbook as Book } from '../yearbook'
import type { HoleScore } from '../engine/types'
import type { TournamentView } from '../select'

const toPar = (n: number): string => (n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`)

/**
 * One card, drawn the way a card is marked.
 *
 * Birdies ringed, bogeys boxed, doubles boxed twice — the notation every man
 * here already reads without being taught it, which is why the key is one line
 * and not a legend.
 */
function Card({ r }: { r: PlayerYear['rounds'][number] }) {
  return (
    <div className="border border-rule mb-2 overflow-x-auto">
      <div className="flex justify-between bg-ink text-paper px-2 py-1">
        <span className="bc-label text-[10px]">{r.label}</span>
        <span className="text-[10px] opacity-70">off {r.courseHandicap}</span>
      </div>
      <table className="w-full border-collapse text-[11px] min-w-max">
        <thead>
          <tr>
            <th className="bc-label text-[9px] text-mute text-left px-1.5 py-0.5 bg-wash">Hole</th>
            {r.cells.map((c) => (
              <th key={c.hole} className="bc-num text-[9px] text-mute w-6 py-0.5 bg-wash">
                {c.hole}
              </th>
            ))}
            <th className="bc-label text-[9px] text-mute px-1.5 bg-wash">Tot</th>
            <th className="bc-label text-[9px] text-mute px-1.5 bg-wash">Net</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="bc-label text-[9px] text-mute px-1.5 bg-wash">Par</td>
            {r.cells.map((c) => (
              <td key={c.hole} className="bc-num text-center text-mute bg-wash">
                {c.par}
              </td>
            ))}
            <td className="bc-num text-center bg-wash">{r.par}</td>
            <td className="bg-wash" />
          </tr>
          <tr>
            <td className="bc-label text-[9px] text-mute px-1.5 bg-wash">Score</td>
            {r.cells.map((c) => {
              if (c.gross == null) return <td key={c.hole} />
              const d = c.gross - c.par
              const ring =
                d <= -1
                  ? 'outline outline-1 outline-signal rounded-full'
                  : d >= 1
                    ? 'outline outline-1 outline-pop'
                    : ''
              return (
                <td key={c.hole} className="text-center py-0.5">
                  <span className={`bc-hand inline-flex w-5 h-5 items-center justify-center ${ring}`}>
                    {c.gross}
                  </span>
                </td>
              )
            })}
            <td className="bc-hand text-center">{r.gross}</td>
            <td className="bc-num text-center text-signal">{toPar(r.net)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function Stat({ k, v, s, tone }: { k: string; v: string; s?: string; tone?: 'money' }) {
  return (
    <div className="bg-paper px-2.5 py-2 flex flex-col">
      <span className="bc-label text-[9px] text-mute">{k}</span>
      <span className={`bc-num text-xl leading-tight ${tone === 'money' ? 'text-signal' : ''}`}>{v}</span>
      {s && <span className="text-[10px] text-mute">{s}</span>}
    </div>
  )
}

export function Yearbook({
  doc,
  scores,
  view,
  me,
}: {
  doc: Tournament
  scores: HoleScore[]
  view: TournamentView
  me: string
}) {
  const book: Book = buildYearbook(doc, scores, view)
  const [who, setWho] = useState(me)
  const [openCards, setOpenCards] = useState(false)
  const [shared, setShared] = useState<string | null>(null)
  const p = book.players.find((x) => x.playerId === who) ?? book.players[0]
  if (!p || book.players.length === 0) return null

  /*
   * Nothing to remember before there is a week to remember. The yearbook is
   * meaningless mid-trip and would only push the schedule — the thing people
   * actually need on a Tuesday — below the fold.
   */
  if (!book.champion) return null

  const share = async () => {
    const text = yearbookText(doc, book, who)
    try {
      if (navigator.share) await navigator.share({ title: doc.name, text })
      else {
        await navigator.clipboard.writeText(text)
        setShared('Copied')
        setTimeout(() => setShared(null), 1600)
      }
    } catch {
      /* a cancelled share sheet is not an error */
    }
  }

  return (
    <section className="border-b-[3px] border-ink">
      {/* The champion, once and large. */}
      <div className="bg-ink text-paper px-4 pt-4 pb-3.5">
        <span className="bc-label text-onink">The Woodsmen {doc.year} · Champion</span>
        <h2 className="bc-display text-[30px] leading-none mt-1">{book.champion.fullName}</h2>
        <p className="bc-label bc-sub text-onink mt-1.5">
          {book.champion.points} points · {book.champion.flight ?? ''} ·{' '}
          {book.champion.championship
            ? `${book.champion.championship.gross} at Bandon Trails`
            : ''}
        </p>
      </div>

      {/* What the week decided, for everybody. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-rule border-b border-rule">
        {book.awards.map((a) => (
          <div key={a.key} className="bg-paper px-2.5 py-2">
            <span className="bc-label text-[9px] text-mute block">{a.title}</span>
            <span className="bc-name text-sm block truncate">{a.name}</span>
            <span className="bc-num text-signal text-[13px]">{a.figure}</span>
            <span className="text-[10px] text-mute block truncate">{a.detail}</span>
          </div>
        ))}
      </div>

      {/* Then you. */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        <span className="bc-label text-mute shrink-0">Your week</span>
        <select
          value={who}
          onChange={(e) => {
            setWho(e.target.value)
            setOpenCards(false)
          }}
          aria-label="Whose year to show"
          className="bc-field bc-tap flex-1 min-w-0 bc-name"
        >
          {book.players.map((x) => (
            <option key={x.playerId} value={x.playerId}>
              {x.position}. {x.name}
              {x.playerId === me ? ' (you)' : ''}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => void share()} className="bc-tap bc-label border-2 border-ink px-2.5 py-2 shrink-0">
          {shared ?? 'Share'}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-px bg-rule mx-4 my-2 border border-rule">
        <Stat k="Finish" v={`${p.position}`} s={p.flight ?? ''} />
        <Stat k="Points" v={`${p.points}`} s={`index ${p.handicapIndex}`} />
        <Stat k="Won" v={`$${p.purse.toLocaleString()}`} s="from the pot" tone="money" />
        <Stat k="Birdies" v={`${p.birdies}`} s="gross" />
        <Stat k="Matches" v={`${p.won}–${p.lost}–${p.halved}`} s="w–l–halved" />
        <Stat k="vs handicap" v={`+${p.vsHandicap}`} s="all week" />
      </div>

      <ul className="px-4 pb-2 text-[13px] text-mute">
        {p.bestNine && (
          <li className="py-0.5">
            <b className="text-ink">Best nine</b> {p.bestNine.gross} ({toPar(p.bestNine.toPar)}) ·{' '}
            {p.bestNine.label}
          </li>
        )}
        {p.bestStretch && (
          <li className="py-0.5">
            <b className="text-ink">Best stretch</b> {p.bestStretch.holes} holes,{' '}
            {p.bestStretch.from}–{p.bestStretch.to} at {toPar(p.bestStretch.toPar)} ·{' '}
            {p.bestStretch.label}
          </li>
        )}
        {p.worstStretch && (
          <li className="py-0.5">
            <b className="text-ink">Worst stretch</b> {p.worstStretch.holes} holes,{' '}
            {p.worstStretch.from}–{p.worstStretch.to} at {toPar(p.worstStretch.toPar)} ·{' '}
            {p.worstStretch.label}
          </li>
        )}
        {p.drought > 0 && (
          <li className="py-0.5">
            <b className="text-ink">Longest drought</b> {p.drought} holes without a birdie
          </li>
        )}
      </ul>

      <button
        type="button"
        onClick={() => setOpenCards((v) => !v)}
        aria-expanded={openCards}
        className="bc-tap w-full px-4 py-2 flex items-center justify-between border-t border-rule text-[13px] text-mute"
      >
        <span>{openCards ? 'Hide the cards' : `All ${p.rounds.length} cards, hole by hole`}</span>
        <span aria-hidden className="text-[11px]">{openCards ? '▴' : '▾'}</span>
      </button>
      {openCards && (
        <div className="px-4 pb-3">
          <p className="text-[10px] text-mute mb-1.5">
            Birdies ringed, bogeys boxed.
          </p>
          {p.rounds.map((r) => (
            <Card key={r.roundId} r={r} />
          ))}
        </div>
      )}
    </section>
  )
}
